import React, { useState, useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import Style from './style.module.css';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  exportPPT,
  exportSingleSlide,
  getPowerBiUrl,
  subscribeToWeeklyReport,
} from '../../gql';
import ModalWrapper from '../shared/ModalWrapper/wrapper';
import TabGroup from '../shared/TabGroup';
import ArticlesIncidentList, {
  parseArticlesJson,
} from './ArticlesIncidentList';

const DEFAULT_DASHBOARD_URL =
  'https://app.powerbi.com/view?r=eyJrIjoiMzlmMThmZTgtNzMyNC00OWU0LWEwMTktZWY1ZWU5MDBhNzBhIiwidCI6IjM4YTZlYWU0LTI4ODktNDZiOC1hMWExLTYzMzY3ZmZlYmE5ZCIsImMiOjF9';

const triggerDownload = (url, filename) => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.name = 'download-frame';
  document.body.appendChild(iframe);

  const link = document.createElement('a');
  link.href = url;
  if (filename) link.download = filename;
  link.target = 'download-frame';
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  document.body.removeChild(iframe);
};

const downloadBlob = (blob, filename) => {
  const blobUrl = URL.createObjectURL(blob);
  triggerDownload(blobUrl, filename);
  URL.revokeObjectURL(blobUrl);
};

const PPTX_MIME =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';

const downloadPptxFromBase64 = (base64, filename) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const blob = new Blob([new Uint8Array(byteNumbers)], { type: PPTX_MIME });
  downloadBlob(blob, filename);
};

const slideFilename = (image, index) => {
  const raw = image?.name || `chart-${index + 1}`;
  const base = raw.replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '_');
  return `${base}.pptx`;
};

const subscribeModalStyles = {
  content: {
    backgroundColor: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    bottom: 'auto',
    left: '50%',
    marginRight: '-50%',
    padding: '32px 28px 24px',
    right: 'auto',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '420px',
    maxWidth: '92%',
    overflow: 'visible',
    boxSizing: 'border-box',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
  },
};

const customStyles = {
  content: {
    backgroundColor: 'transparent',
    border: 'none',
    bottom: 'auto',
    height: '80%',
    left: '50%',
    marginRight: '-50%',
    padding: '20px',
    right: 'auto',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '56%',
    zIndex: 1000,
    overflow: 'hidden',
    boxSizing: 'border-box',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
  },
};

const getApiBaseUrl = () => {
  const graphqlUri =
    process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:4000/graphql';
  return graphqlUri.replace(/\/graphql$/, '');
};

const API_BASE_URL = getApiBaseUrl();

const RAW_DATA_DISPLAY_LIMIT = 200;

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');

  if (!lines.length) {
    return { headers: [], rows: [] };
  }

  const parseLine = (line) => {
    const result = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"' && insideQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map((line) => parseLine(line));

  return { headers, rows };
};

/** Map parsed CSV shape to PrimeReact DataTable columns + row records (stable __id for dataKey). */
const csvShapeToDataTableModel = ({ headers, rows }) => {
  if (!headers.length) {
    return { columns: [], value: [] };
  }
  const columns = headers.map((header, i) => ({
    field: `c${i}`,
    header: header || `Column ${i + 1}`,
  }));
  const value = rows.map((row, rowIndex) => {
    const record = { __id: rowIndex };
    headers.forEach((h, i) => {
      let cellValue = row[i] ?? '';
      if (h === 'District' || h === 'Age' || h === 'victim_age') {
        cellValue = cellValue.split('.')[0];
      }
      record[`c${i}`] = cellValue;
    });
    return record;
  });
  return { columns, value };
};

const ChartDisplay = ({ chartData, pptInfo = {}, subscribeConfig = null }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('charts');
  const [csvTableData, setCsvTableData] = useState({ headers: [], rows: [] });
  const [csvText, setCsvText] = useState('');
  const [isCsvLoading, setIsCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState('');
  const [isExportingSlide, setIsExportingSlide] = useState(false);
  const [exportError, setExportError] = useState('');
  const { data: powerBiData } = useQuery(gql(getPowerBiUrl));
  const dashboardUrl = powerBiData?.powerBiUrl || DEFAULT_DASHBOARD_URL;
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState({
    loading: false,
    error: '',
    success: '',
  });

  const images = chartData?.images || [];
  const csvUrl = chartData?.csvUrl || '';
  const articlesCsv = chartData?.articlesCsv || '';
  const articlesJson = chartData?.articlesJson || '';
  const dataTimestamp = chartData?.dataTimestamp || '';

  const articlesPayload = useMemo(
    () => parseArticlesJson(articlesJson),
    [articlesJson],
  );
  useEffect(() => {
    const fetchCsvData = async () => {
      if (!csvUrl) {
        setCsvTableData({ headers: [], rows: [] });
        setCsvText('');
        return;
      }

      try {
        setIsCsvLoading(true);
        setCsvError('');

        const response = await axios.get(`${API_BASE_URL}${csvUrl}`, {
          responseType: 'text',
        });

        setCsvText(response.data);
        const parsed = parseCSV(response.data);
        setCsvTableData(parsed);
      } catch (error) {
        console.error('CSV fetch failed:', error);
        setCsvText('');
        setCsvError('Unable to load CSV data.');
      } finally {
        setIsCsvLoading(false);
      }
    };

    fetchCsvData();
  }, [csvUrl]);

  const csvPrimeModel = useMemo(
    () => csvShapeToDataTableModel(csvTableData),
    [csvTableData],
  );

  const rawDataTooLarge =
    csvTableData.headers.length > 0 &&
    csvTableData.rows.length > RAW_DATA_DISPLAY_LIMIT;

  if (!chartData)
    return (
      <div
        style={{
          height: '20vh',
          width: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      ></div>
    );

  const handleExportPPT = async () => {
    try {
      if (!images || images.length === 0) return;

      setExportError('');

      const imagePaths = images.map((img) => img.url);

      const response = await axios.post(process.env.REACT_APP_GRAPHQL_URI, {
        query: exportPPT,
        variables: {
          input: {
            images: imagePaths,
            ...pptInfo,
          },
        },
      });

      if (response.data.errors?.length) {
        throw new Error(response.data.errors[0].message);
      }

      const base64 = response.data.data.exportPPT;
      downloadPptxFromBase64(base64, 'Generated_Charts.pptx');
    } catch (error) {
      console.error('Export PPT failed:', error);
      setExportError(error.message || 'Export failed. Please try again.');
    }
  };

  const handleExportSingleSlide = async () => {
    if (selectedImageIndex === null || !images[selectedImageIndex]) return;

    try {
      setIsExportingSlide(true);
      setExportError('');
      const image = images[selectedImageIndex];
      const response = await axios.post(process.env.REACT_APP_GRAPHQL_URI, {
        query: exportSingleSlide,
        variables: {
          input: {
            image: image.url,
            imageIndex: selectedImageIndex,
            ...pptInfo,
          },
        },
      });

      if (response.data.errors?.length) {
        throw new Error(response.data.errors[0].message);
      }

      const base64 = response.data.data.exportSingleSlide;
      downloadPptxFromBase64(base64, slideFilename(image, selectedImageIndex));
    } catch (error) {
      console.error('Export single slide failed:', error);
      setExportError(error.message || 'Slide export failed. Please try again.');
    } finally {
      setIsExportingSlide(false);
    }
  };

  // Monthly reports are emailed once a month and quarterly reports once a
  // quarter; every other periodicity weekly.
  const subscribePeriodicity = subscribeConfig?.dashboardInput?.periodicity;
  const subscribeCadence = ['month', 'quarter'].includes(subscribePeriodicity)
    ? subscribePeriodicity
    : 'week';
  const subscribeCadenceLabel = {
    week: 'weekly',
    month: 'monthly',
    quarter: 'quarterly',
  }[subscribeCadence];

  const openSubscribeModal = () => {
    setSubscribeEmail('');
    setSubscribeStatus({ loading: false, error: '', success: '' });
    setIsSubscribeOpen(true);
  };

  const closeSubscribeModal = () => {
    if (subscribeStatus.loading) return;
    setIsSubscribeOpen(false);
    setSubscribeEmail('');
    setSubscribeStatus({ loading: false, error: '', success: '' });
  };

  const handleSubscribe = async (event) => {
    event.preventDefault();
    if (!subscribeConfig?.dashboardInput) return;

    const email = subscribeEmail.trim();
    if (!email) {
      setSubscribeStatus({
        loading: false,
        error: 'Please enter your email',
        success: '',
      });
      return;
    }

    try {
      setSubscribeStatus({ loading: true, error: '', success: '' });

      const response = await axios.post(process.env.REACT_APP_GRAPHQL_URI, {
        query: subscribeToWeeklyReport,
        variables: {
          input: {
            email,
            queryString: subscribeConfig.queryString || '',
            dashboardInput: subscribeConfig.dashboardInput,
          },
        },
      });

      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      }

      const result = response.data.data?.subscribeToWeeklyReport;
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to subscribe');
      }

      setSubscribeEmail('');
      setSubscribeStatus({
        loading: false,
        error: '',
        success: result.message,
      });
    } catch (error) {
      console.error('Subscribe failed:', error);
      setSubscribeStatus({
        loading: false,
        error: error.message || 'Failed to subscribe',
        success: '',
      });
    }
  };

  const tabs = [
    {
      key: 'charts',
      label: 'Charts',
      content: (
        <div className={Style.tabContainer}>
          <div className={Style.buttonsContainer}>
            <button
              type="button"
              className={Style.actionButton}
              onClick={handleExportPPT}
            >
              Export as PowerPoint
            </button>
            {subscribeConfig?.dashboardInput ? (
              <button
                type="button"
                className={Style.actionButton}
                onClick={openSubscribeModal}
              >
                Subscribe to {subscribeCadenceLabel} updates
              </button>
            ) : null}
          </div>

          {exportError && (
            <div className={Style.errorMessage}>{exportError}</div>
          )}

          <div className={Style.imageGrid}>
            {images?.map((image, index) => (
              <div
                key={index}
                className={Style.imageItem}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img
                  src={`${API_BASE_URL}${image.url}`}
                  alt={`Chart ${index + 1}`}
                  className={Style.chartImage}
                />
              </div>
            ))}
          </div>

          <ModalWrapper
            isOpen={selectedImageIndex !== null}
            closeModal={() => {
              setSelectedImageIndex(null);
              setExportError('');
            }}
            style={customStyles}
          >
            {selectedImageIndex !== null ? (
              <button
                type="button"
                className={`${Style.modalNavButton} ${Style.modalDownloadButton}`}
                aria-label="Download this visual as a PowerPoint slide"
                title="Download slide"
                disabled={isExportingSlide}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExportSingleSlide();
                }}
              >
                <i
                  className={
                    isExportingSlide
                      ? 'pi pi-spin pi-spinner'
                      : 'pi pi-download'
                  }
                  aria-hidden="true"
                />
              </button>
            ) : null}
            {exportError && (
              <div
                className={Style.errorMessage}
                style={{
                  position: 'absolute',
                  top: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                }}
              >
                {exportError}
              </div>
            )}
            <div className={Style.imageModalShell}>
              <img
                src={
                  selectedImageIndex !== null
                    ? `${API_BASE_URL}${images[selectedImageIndex].url}`
                    : ''
                }
                alt={
                  selectedImageIndex !== null
                    ? `Chart ${selectedImageIndex + 1}`
                    : ''
                }
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              {selectedImageIndex !== null && images.length > 1 ? (
                <>
                  <button
                    type="button"
                    className={`${Style.modalNavButton} ${Style.modalNavPrev}`}
                    aria-label="Previous chart"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(
                        (i) => (i - 1 + images.length) % images.length,
                      );
                    }}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className={`${Style.modalNavButton} ${Style.modalNavNext}`}
                    aria-label="Next chart"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex((i) => (i + 1) % images.length);
                    }}
                  >
                    ›
                  </button>
                </>
              ) : null}
            </div>
          </ModalWrapper>
        </div>
      ),
    },

    {
      key: 'news',
      label: 'News',
      content: (
        <div className={Style.tabContainer}>
          <div className={Style.buttonsContainer}>
            {articlesCsv.trim() ? (
              <button
                type="button"
                className={Style.actionButton}
                onClick={() => {
                  const blob = new Blob([articlesCsv], {
                    type: 'text/csv;charset=utf-8;',
                  });
                  downloadBlob(blob, 'related-articles.csv');
                }}
              >
                Download CSV
              </button>
            ) : null}
          </div>

          <ArticlesIncidentList articlesPayload={articlesPayload} />
        </div>
      ),
    },
    {
      key: 'raw-data',
      label: 'Raw Data',
      content: (
        <div className={Style.tabContainer}>
          <div className={Style.buttonsContainer}>
            {csvUrl && (
              <button
                type="button"
                className={Style.actionButton}
                disabled={isCsvLoading || !csvText}
                onClick={() => {
                  const filename = csvUrl.split('/').pop() || 'raw-data.csv';
                  const blob = new Blob([csvText], {
                    type: 'text/csv;charset=utf-8;',
                  });
                  downloadBlob(blob, filename);
                }}
              >
                Download CSV
              </button>
            )}
          </div>

          {isCsvLoading && (
            <div className={Style.emptyState}>Loading CSV data...</div>
          )}

          {csvError && <div className={Style.errorMessage}>{csvError}</div>}

          {!isCsvLoading && !csvError && csvTableData.headers.length > 0 && (
            <div className={Style.rawDataCard}>
              <div className={Style.rawDataHeader}>
                <div>
                  <h4 className={Style.rawDataTitle}>Raw Data</h4>
                  <p className={Style.rawDataMeta}>
                    {csvTableData.rows.length} rows ·{' '}
                    {csvTableData.headers.length} columns
                  </p>
                </div>
              </div>

              {rawDataTooLarge ? (
                <div className={Style.emptyState}>
                  Raw data preview is limited to {RAW_DATA_DISPLAY_LIMIT} rows.
                  Download the CSV to access the full dataset.
                </div>
              ) : (
                <div className={Style.tableOuter}>
                  <div className={Style.tableWrapper}>
                    <DataTable
                      value={csvPrimeModel.value}
                      dataKey="__id"
                      scrollable
                      scrollHeight="560px"
                      stripedRows
                      showGridlines
                      size="small"
                      className={Style.primeDataTable}
                    >
                      {csvPrimeModel.columns.map((col) => (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          sortable
                          body={(row) => {
                            const text = row[col.field] || '-';
                            return (
                              <span className={Style.cellText} title={text}>
                                {text}
                              </span>
                            );
                          }}
                        />
                      ))}
                    </DataTable>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isCsvLoading && !csvError && csvTableData.headers.length === 0 && (
            <div className={Style.emptyState}>No CSV data available.</div>
          )}
        </div>
      ),
    },
    {
      key: 'dashboard',
      label: 'Dashboard',
      content: (
        <div className={Style.tabContainer}>
          <div className={Style.rawDataCard}>
            <div className={Style.rawDataHeader}>
              <div>
                <h4 className={Style.rawDataTitle}>Dashboard</h4>
              </div>
            </div>
            <div className={Style.dashboardContent}>
              <p className={Style.dashboardDescription}>
                Click the button below to launch a data analytics dashboard to
                help you take a deeper dive into any trends uncovered by your
                report. This dashboard offers greater interactivity to conduct
                targeted geographic analysis, explore the interaction between
                multiple variables, and run dynamic comparisons of trends to
                examine the efficacy of any potential anti-violence or public
                safety interventions.
              </p>
              <a
                href={dashboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={Style.actionButton}
              >
                Dashboard
              </a>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'sources',
      label: 'Sources',
      content: (
        <div className={Style.tabContainer}>
          <div className={Style.rawDataCard}>
            <div className={Style.rawDataHeader}>
              <div>
                <h4 className={Style.rawDataTitle}>Sources</h4>
                <p className={Style.rawDataMeta}>
                  {
                    'Open data sources for gun violence and crime data in Philadelphia, PA include, but may not be limited to:'
                  }
                </p>
              </div>
            </div>

            {dataTimestamp && (
              <div
                style={{
                  padding: '1rem 1.25rem',
                  fontSize: '0.875rem',
                  color: '#666',
                }}
              >
                Data as of:{' '}
                {new Date(dataTimestamp).toLocaleString('en-US', {
                  dateStyle: 'long',
                  timeStyle: 'medium',
                })}
              </div>
            )}

            <div style={{ padding: '1rem 1.25rem' }}>
              <div style={{ wordBreak: 'break-word' }}>
                <ul
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <li>
                    <a
                      href="https://opendataphilly.org/datasets/crime-incidents/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: 'break-word' }}
                    >
                      OpenDataPhilly.org: datasets/crime-incidents
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://opendataphilly.org/datasets/shooting-victims/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: 'break-word' }}
                    >
                      OpenDataPhilly.org: datasets/shooting-victims
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://github.com/PhilaController/gun-violence-dashboard-data"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: 'break-word' }}
                    >
                      PhilaController: gun-violence-dashboard-data
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const activeTabConfig = tabs.find((tab) => tab.key === activeTab);

  if (images && images.length > 0) {
    return (
      <div className={Style.chartGallery}>
        <h3 className={Style.galleryTitle}>Generated Reports</h3>

        <TabGroup tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {activeTabConfig?.content}

        <ModalWrapper
          isOpen={isSubscribeOpen}
          closeModal={closeSubscribeModal}
          style={subscribeModalStyles}
          closeButtonColor="#111827"
        >
          <form className={Style.subscribeModal} onSubmit={handleSubscribe}>
            <h3 className={Style.subscribeTitle}>
              Subscribe to {subscribeCadenceLabel} updates
            </h3>
            <p className={Style.subscribeCopy}>
              Enter your email to receive a PDF of this report every{' '}
              {subscribeCadence}
            </p>
            <label
              className={Style.subscribeLabel}
              htmlFor="weekly-subscribe-email"
            >
              Email
            </label>
            <input
              id="weekly-subscribe-email"
              className={Style.subscribeInput}
              type="email"
              autoComplete="email"
              value={subscribeEmail}
              onChange={(event) => setSubscribeEmail(event.target.value)}
              placeholder="you@example.com"
              disabled={
                subscribeStatus.loading || Boolean(subscribeStatus.success)
              }
              required
            />
            {subscribeStatus.error ? (
              <div className={Style.subscribeError}>
                {subscribeStatus.error}
              </div>
            ) : null}
            {subscribeStatus.success ? (
              <div className={Style.subscribeSuccess}>
                {subscribeStatus.success}
              </div>
            ) : null}
            <button
              type="submit"
              className={Style.subscribeSubmit}
              disabled={
                subscribeStatus.loading || Boolean(subscribeStatus.success)
              }
            >
              {subscribeStatus.loading ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
        </ModalWrapper>
      </div>
    );
  }

  return <p className={Style.emptyState}>No charts generated yet.</p>;
};

export default ChartDisplay;
